import { Box, Center, Grid, GridItem, Spinner, Stack, Text, useColorModeValue, VStack } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Patch, useBulkUpdateRequestLineItems } from '../hooks/lineItem/useBulkUpdateRequestLineItems';
import { useRequestLineItems } from '../hooks/lineItem/useRequestLineItems';
import { QuoteLineItem } from '../utils/types';
import { LineItemRow } from './QuoteCard/LineItemRow';

type Props = {
  requestId: number;
};

type MergedLineItem = QuoteLineItem & {
  peerIds: number[];
  compareKey: string;
  category: 'airline' | 'inland';
  providerId?: string | number | null;
  quoteVariantId?: number | null;
};

const getLineItemCompareKey = (lineItem: QuoteLineItem) => {
  const lineItemWithQuoteVariant = lineItem as QuoteLineItem & {
    quote_variant?: {
      as_request_inland_quote_variant_id?: number | null;
    };
  };
  const isInland = !!lineItemWithQuoteVariant?.quote_variant?.as_request_inland_quote_variant_id;
  const category = isInland ? 'inland' : 'airline';
  const keys = [
    category,
    lineItem.label,
    lineItem.type,
    lineItem.cost,
    lineItem.per_unit_cost_type,
    lineItem.per_unit_cost,
    lineItem.per_unit_count_type,
    lineItem.per_unit_count,
  ];
  return keys.join('-');
};

const getSingleLineItemLabel = (lineItem: MergedLineItem) => {
  const quoteType = lineItem.category === 'airline' ? 'Airline' : 'Inland';
  const providerId = lineItem.peerIds.length === 1 && lineItem.providerId ? `: ${lineItem.providerId}` : '';
  return `${quoteType}${providerId}`;
};

/** Airline before inland; within a category, rows with more peers (shared markup) first; then by provider. */
const compareMergedLineItems = (a: MergedLineItem, b: MergedLineItem): number => {
  if (a.category !== b.category) {
    return a.category === 'airline' ? -1 : 1;
  }
  const peerDiff = b.peerIds.length - a.peerIds.length;
  if (peerDiff !== 0) return peerDiff;
  return String(a.providerId ?? '').localeCompare(String(b.providerId ?? ''));
};

export const LineItemsList = ({ requestId }: Props) => {
  const { lineItems, isLoading } = useRequestLineItems({ requestId });
  const [bulkUpdate, isBulkUpdating] = useBulkUpdateRequestLineItems({ requestId });
  const [mergedLineItems, setMergedLineItems] = useState<Map<string, MergedLineItem>>(new Map());

  const handleBulkUpdate = useCallback(
    (sourceLineItemId: number, peerIds: number[], patch: Patch) => bulkUpdate(sourceLineItemId, peerIds, patch),
    [bulkUpdate],
  );

  const bgHeader = useColorModeValue('gray.50', 'gray.700');
  const bgColor = useColorModeValue('white', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.500');

  useEffect(() => {
    if (isBulkUpdating) return;
    setMergedLineItems(
      lineItems.reduce((acc, lineItem) => {
        const lineItemWithQuoteVariant = lineItem as QuoteLineItem & {
          quote_variant?: {
            as_request_inland_quote_variant_id?: number | null;
            quote?: {
              provider_id?: string | number | null;
            };
          };
        };
        const lineItemCompareKey = getLineItemCompareKey(lineItem);
        const isInland = !!lineItemWithQuoteVariant?.quote_variant?.as_request_inland_quote_variant_id;
        const category = isInland ? 'inland' : 'airline';

        if (acc.has(lineItemCompareKey)) {
          acc.get(lineItemCompareKey)?.peerIds.push(lineItem.id);
        } else {
          acc.set(lineItemCompareKey, {
            ...lineItem,
            peerIds: [lineItem.id],
            compareKey: lineItemCompareKey,
            category,
            providerId: lineItemWithQuoteVariant?.quote_variant?.quote?.provider_id,
            quoteVariantId: lineItem.quote_variant_id,
          });
        }
        return acc;
      }, new Map<string, MergedLineItem>()),
    );
  }, [lineItems, isBulkUpdating]);

  const sortedRows = useMemo(() => [...mergedLineItems.values()].sort(compareMergedLineItems), [mergedLineItems]);

  if (!lineItems?.length || isLoading) return null;

  return (
    <Box maxW="full" overflowX="auto" sx={{ WebkitOverflowScrolling: 'touch' }}>
      <VStack
        borderRadius={8}
        borderWidth={1}
        borderColor={borderColor}
        align="stretch"
        overflow="hidden"
        spacing={0}
        minW="720px"
      >
        <Stack w="full" spacing={0} bg={bgHeader}>
          <Grid templateColumns="repeat(28, 1fr)" w="full" gap={1} py={1}>
            <GridItem colSpan={1} />
            <GridItem colSpan={4}>
              <Text textStyle="label" px="2">
                Quote
              </Text>
            </GridItem>
            <GridItem colSpan={6}>
              <Text textStyle="label" px="2">
                Label
              </Text>
            </GridItem>
            <GridItem colSpan={5}>
              <Text textStyle="label" px="3.5">
                Type
              </Text>
            </GridItem>
            <GridItem colSpan={3}>
              <Text textStyle="label" align="right" px="1">
                Unit
              </Text>
            </GridItem>
            <GridItem colSpan={4}>
              <Text textStyle="label" align="right" px="1">
                Rate
              </Text>
            </GridItem>
            <GridItem colSpan={3}>
              <Text textStyle="label" align="right" px="1">
                Cost
              </Text>
            </GridItem>
            <GridItem colSpan={1} />
            <GridItem colSpan={1} />
          </Grid>
          <Stack w="full" spacing={0} bgColor={bgColor}>
            {sortedRows.map((row) => (
              <LineItemRow
                key={row.compareKey}
                lineItem={row}
                peerIds={row.peerIds}
                isByRequest
                onBulkUpdate={handleBulkUpdate}
                requestId={requestId}
                quoteCategory={row.category}
                quoteLabel={getSingleLineItemLabel(row)}
                quoteVariantId={row.quoteVariantId ?? undefined}
              />
            ))}
          </Stack>
          <Show if={isLoading}>
            <Center w="full" py={4}>
              <Spinner size="sm" />
            </Center>
          </Show>
        </Stack>
      </VStack>
    </Box>
  );
};
